import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ToastController } from '@ionic/angular';
import { Subscription } from 'rxjs';
import { NetworkService } from '../../services/network.service';
import { distinctUntilChanged } from 'rxjs/operators';

/**
 * Offline Indicator Component
 * Shows a toast notification when the device goes offline or comes back online.
 * This component should be included in the app component to monitor network status globally.
 */
@Component({
  selector: 'app-offline-indicator',
  templateUrl: './offline-indicator.component.html',
  styleUrls: ['./offline-indicator.component.scss'],
  standalone: true,
  imports: [CommonModule]
})
export class OfflineIndicatorComponent implements OnInit, OnDestroy {
  private readonly networkService = inject(NetworkService);
  private readonly toastController = inject(ToastController);
  private networkSubscription?: Subscription;
  private lastStatus: boolean | null = null;
  private currentToast: HTMLIonToastElement | null = null;

  ngOnInit(): void {
    // Subscribe to network status changes
    this.networkSubscription = this.networkService.onlineStatus$
      .pipe(distinctUntilChanged())
      .subscribe((isOnline: boolean) => {
        // Only show toast when status actually changes
        if (this.lastStatus !== null && this.lastStatus !== isOnline) {
          this.showStatusToast(isOnline);
        }
        this.lastStatus = isOnline;
      });

    // Set initial status
    this.lastStatus = this.networkService.isOnline();
  }

  ngOnDestroy(): void {
    if (this.networkSubscription) {
      this.networkSubscription.unsubscribe();
    }
    // Dismiss any open toast
    if (this.currentToast) {
      this.currentToast.dismiss();
    }
  }

  /**
   * Show a toast notification for network status change
   */
  private async showStatusToast(isOnline: boolean): Promise<void> {
    // Dismiss any existing toast first
    if (this.currentToast) {
      await this.currentToast.dismiss();
    }

    const message = isOnline
      ? 'Internet connection restored'
      : 'No internet connection. The app is running in offline mode.';

    const toast = await this.toastController.create({
      message,
      duration: isOnline ? 3000 : 0, // Online: auto-dismiss, Offline: stay until dismissed
      position: 'top',
      color: isOnline ? 'success' : 'warning',
      mode: 'ios',
      buttons: isOnline
        ? [{ text: 'OK', role: 'cancel' }]
        : [{ text: 'OK', role: 'cancel' }],
      cssClass: 'offline-indicator-toast'
    });

    this.currentToast = toast;
    await toast.present();

    // Clear reference when toast is dismissed
    toast.onDidDismiss().then(() => {
      if (this.currentToast === toast) {
        this.currentToast = null;
      }
    });
  }
}
